import { useMutation, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import useAuthContext from "../../hooks/useAuthContext";
import { HistoryByUserService, UserCreateService, UserDeleteService, UserUpdateService, UsersAllServices, UserByIdService, UserPointsService } from "./UsersService";
import { useToast } from "@chakra-ui/toast";

const queryUsers = () => {
  const { user } = useAuthContext();
  const queryClient = useQueryClient();
  const toast = useToast();

  const usersAll = ({ search = '' }) => {
    const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage, ...rest } = useInfiniteQuery(
      ['usersAll', search],
      ({ pageParam = 1 }) => UsersAllServices({ token: user?.access_token, page: pageParam, search }),
      {
        getNextPageParam: (lastPage) => {
          const meta = lastPage?.data?.data?.meta;
          return meta?.current_page < meta?.last_page ? meta.current_page + 1 : undefined;
        },
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5,
      }
    );

    const allItems = data?.pages?.flatMap(p => p?.data?.data?.data || []) ?? [];
    const lastMeta = data?.pages?.at(-1)?.data?.data?.meta;

    return { data: { data: allItems, meta: lastMeta }, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, ...rest };
  };

  const userById = (id) => {
    const { data, isLoading, isError, isSuccess } = useQuery(
      ['usersById'], () => UserByIdService({ token: user?.access_token, id }), {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    }
    );

    const dataResult = data?.data.data || [];

    return { data: dataResult, isLoading, isError, isSuccess };
  };

  const usersUngrouped = ({ search = '' }) => {
    const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage, ...rest } = useInfiniteQuery(
      ['users-ungrouped', search],
      ({ pageParam = 1 }) => UsersAllServices({ token: user?.access_token, page: pageParam, search, group_id: null }),
      {
        getNextPageParam: (lastPage) => {
          const meta = lastPage?.data?.data?.meta;
          return meta?.current_page < meta?.last_page ? meta.current_page + 1 : undefined;
        },
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5,
      }
    );

    const allItems = data?.pages?.flatMap(p => p?.data?.data?.data || []) ?? [];
    const lastMeta = data?.pages?.at(-1)?.data?.data?.meta;

    return { data: { data: allItems, meta: lastMeta }, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, ...rest };
  };

  const usersByGroupWithUngrouped = ({ search = '', group_id_edited }) => {
    const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage, ...rest } = useInfiniteQuery(
      ['users-ungrouped-by-group', search, group_id_edited],
      ({ pageParam = 1 }) => UsersAllServices({ token: user?.access_token, page: pageParam, search, group_id_edited }),
      {
        getNextPageParam: (lastPage) => {
          const meta = lastPage?.data?.data?.meta;
          return meta?.current_page < meta?.last_page ? meta.current_page + 1 : undefined;
        },
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5,
      }
    );

    const allItems = data?.pages?.flatMap(p => p?.data?.data?.data || []) ?? [];
    const lastMeta = data?.pages?.at(-1)?.data?.data?.meta;

    return { data: { data: allItems, meta: lastMeta }, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, ...rest };
  };

  const userCreate = () => {
    const { mutate, ...rest } = useMutation(
      (data) => UserCreateService({ token: user?.access_token, data }),
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['usersAll'] });
          toast({
            title: 'Usuario creado',
            description: 'El usuario se creó correctamente',
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
          });
        },
        onError: (error) => {
          if (error.status === 433) {
            return toast({
              title: 'El correo electrónico ya existe',
              status: 'error',
              duration: 2000,
              isClosable: true,
              position: 'top-right',
            });
          }
          toast({
            title: 'Error',
            description: 'No se pudo crear el usuario, inténtelo mas tarde',
            status: 'error',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
          });
        },
      }
    );
    return { mutate, ...rest };
  };

  const userUpdate = () => {
    const { mutate, ...rest } = useMutation(
      (data) => UserUpdateService({ token: user?.access_token, data }),
      {
        onSuccess: (_, opt) => {
          if (opt.adsFlag) return;
          queryClient.invalidateQueries({ queryKey: ['usersAll'] });
          queryClient.invalidateQueries({ queryKey: ['users-ungrouped'] });
          queryClient.invalidateQueries({ queryKey: ['users-ungrouped-by-group'] });
          toast({
            title: 'Usuario actualizado',
            description: 'El usuario se actualizó correctamente',
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
          });
        },
        onError: (error) => {
          if (error.status === 433) {
            toast({
              title: 'El correo electrónico ya existe',
              status: 'error',
              duration: 2000,
              isClosable: true,
              position: 'top-right',
            });
          } else {
            toast({
              title: 'Error',
              description: 'No se pudo editar el usuario, inténtelo mas tarde',
              status: 'error',
              duration: 2000,
              isClosable: true,
              position: 'top-right',
            });
          }
        },
      }
    );

    return { mutate, ...rest };
  };

  const userDelete = () => {
    const { mutate, ...rest } = useMutation(
      ({ ids }) => UserDeleteService({ token: user?.access_token, ids }),
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['usersAll'] });
          queryClient.invalidateQueries({ queryKey: ['users-ungrouped'] });
          queryClient.invalidateQueries({ queryKey: ['users-ungrouped-by-group'] });
          toast({
            title: 'Usuario eliminado',
            description: 'El usuario se eliminó correctamente',
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
          });
        },
        onError: () => {
          toast({
            title: 'Error',
            description: 'No se pudo eliminar el usuario, inténtelo mas tarde',
            status: 'error',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
          });
        },
      }
    );

    return { mutate, ...rest };
  };

  const userHistory = (userId) => {
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, ...rest } = useInfiniteQuery(
      ['userHistory', userId],
      ({ pageParam = 1 }) => HistoryByUserService({ token: user?.access_token, page: pageParam, userId }),
      {
        enabled: userId !== null && userId !== undefined,
        getNextPageParam: (lastPage) => {
          const meta = lastPage?.data?.data?.meta;
          return meta?.current_page < meta?.last_page ? meta.current_page + 1 : undefined;
        },
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5,
      }
    );

    const contactsHistory = [];
    (data?.pages ?? []).forEach(page => {
      (page?.data?.data?.data || []).forEach((item) => {
        contactsHistory.push({
          date: item.created_at,
          contact: item.contact,
          plan: item.recharge.rechargeable_object?.name || '',
          rechargeId: item.recharge_id,
          price_pay: item.price_pay,
          currency: item.recharge.currency,
        });
      });
    });

    const lastMeta = data?.pages?.at(-1)?.data?.data?.meta;

    return {
      data: { data: contactsHistory, meta: lastMeta },
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      ...rest,
    };
  };

  const userPoints = () => {
    const { data, isLoading, isError, ...rest } = useQuery(
      ['userPoints'], () => UserPointsService({ token: user?.access_token })
    );

    const dataResult = data?.data || {};

    return { data: dataResult, isLoading, isError, ...rest };
  };

  return { usersAll, userById, usersByGroupWithUngrouped, usersUngrouped, userCreate, userUpdate, userDelete, userHistory, userPoints };
};

export default queryUsers;
